import $ from 'jquery';
import {
  A11y,
  Keyboard,
  Lazy,
  Navigation,
  Pagination,
  Swiper,
} from 'swiper/dist/js/swiper.esm.js';
import throttle from 'lodash/throttle.js';

// Extracted long CSS Classes for better minification
export const cssClassLazyloadOnSlide = 'lazyload-on-slide';
export const cssClassSwiperWrapper = 'swiper-wrapper';
export const cssClassSwiperSlide = 'swiper-slide';
export const cssClassSwiperButtonNext = 'swiper-button-next';
export const cssClassSwiperButtonPrev = 'swiper-button-prev';
export const cssClassSwiperPagination = 'swiper-pagination';
export const cssClassSwiperDuplicate = 'swiper-slide-duplicate';
const cssClassSwiperDotsActive = 'swiper-pagination-bullet-active';
const cssClassSwiperDotsInactive = 'swiper-pagination-bullet-inactive';
const cssClassSwiperDotsNext = 'swiper-pagination-bullet-next';
const dotsMax = 5;
let dotSize = 0;

export default class Slider {
  constructor(options) {
    // Resolve options
    this.options = {
      namespace: '',
      sliderNode: null,
      swiperConfigOverride: {},
      $wrapper: null,
      callbacks: {
        onInitStart() {},
        onDestroy() {}
      },
    };
    Object.assign(this.options, options);

    // Declare
    this.namespace = this.options.namespace;
    this.slider = this.options.sliderNode;
    this.$slider = $(this.slider);
    this.$wrapper = this.options.$wrapper || this.$slider.find(`.${cssClassSwiperWrapper}`);
    this.$buttonNext = this.$slider.find(`.${cssClassSwiperButtonNext}`);
    if (this.$buttonNext.length === 0) {
      this.$buttonNext = this.$slider.nextAll(`.${cssClassSwiperButtonNext}`);
    }
    this.$buttonPrev = this.$slider.find(`.${cssClassSwiperButtonPrev}`);
    if (this.$buttonPrev.length === 0) {
      this.$buttonPrev = this.$slider.nextAll(`.${cssClassSwiperButtonPrev}`);
    }
    this.$pagination = this.$slider.find(`.${cssClassSwiperPagination}`);
    if (this.$pagination.length === 0) {
      this.$pagination = this.$slider.nextAll(`.${cssClassSwiperPagination}`);
    }
    this.callbacks = this.options.callbacks;

    // Update default config
    this.config = Object.assign({
      keyboard: true,
      loop: true,
      lazy: {
        loadPrevNext: true,
        loadOnTransitionStart: true,
      },
      navigation: {
        nextEl: this.$buttonNext.get(0),
        prevEl: this.$buttonPrev.get(0),
        hiddenClass: 'd-none',
      },
      pagination: {
        el: this.$pagination.get(0),
        type: 'custom',
        renderCustom: _renderCustomPagination,
      },
      on : {
        lazyImageLoad: lazyLoadImage,
        paginationRender(swiper) {
          attachPaginationEvents(swiper);
        },
      },
      breakpointsInverse: true,
    }, this.options.swiperConfigOverride);
  }

  init() {
    Swiper.use([
      A11y,
      Keyboard,
      Lazy,
      Navigation,
      Pagination,
    ]);

    this.callbacks.onInitStart.call(this);
    new Swiper(this.slider, this.config);
    this.showNavigation();
  }

  /**
   * @return {boolean} true if slider was destroyed
   */
  destroy() {
    if (typeof this.slider.swiper !== 'undefined' && this.slider.swiper !== null) {
      this.slider.swiper.destroy();
      this.hideNavigation();
      this.callbacks.onDestroy.call(this);

      return true;
    }

    return false;
  }

  /**
   * @return {boolean} true if slider instance is initialized
   */
  isInitialized() {
    return sliderIsInitialized(this.slider);
  }

  /**
   * Destroys the slider and calculates if the initial slides doesn't fit into the wrapper
   * @return {boolean} true if not all slides fit into the wrapper
   */
  slidesDontFitIntoWrapper() {
    /*
     * It is currently necessary to destroy the slider first, since a slider
     * might using the 'loop' option by default. With this option enabled,
     * Swiper will add additional slides to the Slider which would cause this
     * function to fail.
     */
    this.destroy();
    const scrollWidth = this.$wrapper[0].scrollWidth;
    const offsetWidth = this.$wrapper[0].offsetWidth;

    return scrollWidth > offsetWidth;
  }

  attachResize() {
    $(window).on(`resize.${this.namespace}`, throttle(() => {
      _recalculateState.call(this);
    }, 1000));
  }

  detachResize() {
    $(window).off(`resize.${this.namespace}`);
  }

  showNavigation() {
    this.$buttonNext.removeClass('d-none');
    this.$buttonPrev.removeClass('d-none');
    this.$pagination.removeClass('d-none');
  }

  hideNavigation() {
    this.$buttonNext.addClass('d-none');
    this.$buttonPrev.addClass('d-none');
    this.$pagination.addClass('d-none');
  }
}

function _recalculateState() {
  const slidesDontFitInSlider = this.slidesDontFitIntoWrapper();
  const swiperIsActive = this.isInitialized();

  if (swiperIsActive === false && slidesDontFitInSlider) {
    this.init();
  }
}

/**
 * @param {object} slider node of .swiper-container
 * @return {boolean} true if slider instance is initialized
 */
export function sliderIsInitialized(slider) {
  return slider.swiper !== null && slider.swiper !== undefined;
}

function _renderCustomPagination(swiper, current, total) {
  const $pagination = $(swiper.pagination.$el);

  if (total <= 1) {
    return; // Don't render when one single slide
  }

  if (
      // Pagination already exists
      $pagination.children().length >= 1 &&
      // Same amount of dots
      swiper.$dotsCount === total
  ) {
    _updatePaginationDots.call(Slider, swiper, current, total);
    return; // Don't render twice
  }

  $pagination.data('customPaginationAttached', false);
  return _renderDynamicPaginationDots.call(Slider, swiper, current, total);
}

export function attachPaginationEvents(swiper) {
  const $pagination = $(swiper.pagination.$el);

  if ($pagination.data('customPaginationAttached') === true) {
    return;
  }

  // Set
  swiper.$dotsWrapper = $pagination.children();
  swiper.$dots = swiper.$dotsWrapper.children();
  swiper.$dotsCount = swiper.$dots.length;

  // Calculate sizes
  if (dotSize <= 0) {
    // Ensure dot size can be calculated
    $pagination.css({
      display: 'block',
      height: '1px',
      visibility: 'hidden',
    });

    // Calculate
    dotSize = swiper.$dots.eq(0).outerWidth(true);

    // Reset
    $pagination.css({
      display: '', // '' instead of null for IE 11 compatibilty
      height: '',
      visibility: '',
    });
  }
  $pagination.css(
      'width',
      dotSize * dotsMax + 'px'
  );

  $pagination.data('customPaginationAttached', true);
}

function _renderDynamicPaginationDots(swiper, current, total) {
  let dotsHtml = '';

  for (let index = 0; index < total; index++) {
    const slideNumber = index + 1;

    dotsHtml += '<span class="' +
        'swiper-pagination-bullet' +
        (slideNumber === current ? ' ' + cssClassSwiperDotsActive : '') +
        ((total > dotsMax && slideNumber > dotsMax) ? ' ' + cssClassSwiperDotsInactive : '') +
        ((total > dotsMax && slideNumber === dotsMax) ? ' ' + cssClassSwiperDotsNext : '') +
        '" ' +
        'data-index="' + index + '" ' +
        'tabindex="0" ' +
        'aria-label="Gehe zu Element ' + slideNumber + '"></span>';
  }

  $(swiper.pagination.$el).addClass('swiper-pagination-bullets');

  return '<div class="swiper-pagination-customer-wrapper">' + dotsHtml + '</div>';
}

function _updatePaginationDots(swiper, current, total) {
  const currentIndex = current - 1;

  // Update active state
  swiper.$dots.removeClass(cssClassSwiperDotsActive);
  swiper.$dots.eq(currentIndex).addClass(cssClassSwiperDotsActive);

  if (total <= dotsMax) {
    return;
  }

  /*
   * Dynamic dots
   */
  swiper.$dots.addClass(cssClassSwiperDotsInactive);
  swiper.$dots.removeClass(cssClassSwiperDotsNext);

  const isReachingBeginning = current < dotsMax - 1;
  const isInBetween = total >= dotsMax + 2 && current >= dotsMax - 1 && current <= total - (dotsMax - 2);
  const isReachingEnd = current > total - (dotsMax - 2);
  let targetTransform = 0;

  if (isInBetween) {
    targetTransform = dotSize * (currentIndex - (dotsMax - 3));
  }
  if (isReachingEnd) {
    targetTransform = dotSize * (total - dotsMax);
  }

  swiper.$dotsWrapper.css(
      'transform',
      'translateX(-' + (targetTransform >= 0 ? targetTransform : 0) + 'px)'
  );

  swiper.$dots.each((index, dot) => {
    const $dot = $(dot);
    const sliderNumber = index + 1;

    if ($dot.hasClass(cssClassSwiperDotsActive) === true) {
      $dot.removeClass(cssClassSwiperDotsInactive);
    }

    // When dotsMax == 5 - then till 4th
    if (isReachingBeginning) {
      // 1st to 4th
      if (sliderNumber <= dotsMax) {
        $dot.removeClass(cssClassSwiperDotsInactive);
      }
      // 5th
      if (sliderNumber === dotsMax) {
        $dot.addClass(cssClassSwiperDotsNext);
      }
    }

    // When dotsMax == 5 - when more then 7 && current is between 4th and 4th to last
    if (isInBetween) {
      // 1st lvl sibling
      if (sliderNumber === current - 1 || sliderNumber === current + 1) {
        $dot.removeClass(cssClassSwiperDotsInactive);
      }

      // 2nd lvl sibling
      if (sliderNumber === current - 2 || sliderNumber === current + 2) {
        $dot.removeClass(cssClassSwiperDotsInactive);
        $dot.addClass(cssClassSwiperDotsNext);
      }
    }

    // When dotsMax == 5 - then from 3rd to last
    if (isReachingEnd) {
      // Last 5
      if (sliderNumber > total - dotsMax) {
        $dot.removeClass(cssClassSwiperDotsInactive);
      }
      // 5th to last
      if (sliderNumber === total - (dotsMax - 1)) {
        $dot.addClass(cssClassSwiperDotsNext);
      }
    }
  });
}

/**
 * Used within Swiper initialisation for the lazyImageLoad-event.
 * @param {object} swiper instance
 * @param {object} slide node of .swiper-slide
 */
export function lazyLoadImage(swiper, slide) {
  const $slide = $(slide);
  const $slideImage = $slide.find(`.${cssClassLazyloadOnSlide}`);

  if ($slideImage.length && $slideImage.hasClass('lazyload') === false) {
    $slideImage.addClass('lazyload lazypreload');
    $slideImage.removeClass(cssClassLazyloadOnSlide);
  }
}
